import React from 'react'
import commonCodebase from 'common-recruspin-codebase'
import { Link as RouterLink } from 'react-router-dom'
import {
  Container,
  Grid,
  Typography,
  Link,
  Box,
  styled,
  Card,
  CardContent,
  CardHeader,
  Chip,
} from '@mui/material'
import { FormattedMessage } from 'react-intl'
import NoProvidedCustomerEmail from '../../../../components/NoProvidedCustomerEmail'
import CopyLinkBtn from '../../ReadEditOrder/components/CopyLinkButton'
import JobDetailsMenu from '../../ReadEditOrder/components/JobDetailsMenu'
import ShareVacancy from '../../ReadEditOrder/components/ShareVacancy'
import SendPreviewLink from '../components/SendPreviewLink'
import FileViewRow from '../../../../components/File/FileViewRow'
import useInternalUserPermissions from '../../../../hooks/useInternalUserPermissions'
import { Iprops, TfileItem, TimageItem } from '../../ReadEditOrder/types'
import { convertValuesToLabels, hasPermission } from '../../../../utils/tools'
import { customerTypes } from '../../../Customers/components/CreateCustomer'
import { useSelector } from '../../../../store/store'
import { isCustomer } from '../../../../constants'

const { statuses } = commonCodebase.orders

// Custom styled components for OrderTree context
const StyledContainer = styled(Container)(() => ({
  padding: 0,
  margin: '0 auto',
  maxWidth: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}))

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: '8px',
  overflow: 'visible',
  boxShadow: 'none',
  border: '1px solid #e0e0e0',
  width: '100%',
}))

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiCardHeader-title': {
    fontSize: '1rem',
    fontWeight: 600,
  },
}))

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  '&:last-child': {
    paddingBottom: theme.spacing(3), // Override default padding for CardContent
  },
}))

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}))

const FieldValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
}))

const StyledGridContainer = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1, 0),
}))

const StyledLink = styled(Link)(() => ({
  wordBreak: 'break-all',
  overflowWrap: 'break-word',
  display: 'inline-block',
  maxWidth: '100%',
  lineHeight: 1.5,
}))

const ReadOrderTree: React.FC<Iprops> = ({ order }) => {
  const { permissions, userInfo } = useSelector((state) => state.user)
  const permission = hasPermission(permissions?.orderDetailsFields)
  const previewFiles = [
    ...(order.previews || []),
    ...(order.previewsAudio || []),
    ...(order.previewsVideo || []),
  ]
  const customerHasEmail = order?.customer?.emails
    ? JSON.parse(order?.customer.emails).length
    : null

  // Helper function to safely get customer email
  const getCustomerEmail = () => {
    try {
      // If customer is a resellerCustomer with a parent reseller, use reseller's email
      if (order?.customer?.type === 'resellerCustomer' && order?.customer?.connectedReseller) {
        const reseller = order.customer.connectedReseller

        // Use the parent reseller's email with the same priority logic
        if (reseller.defaultPreviewEmail) {
          return reseller.defaultPreviewEmail
        }
        if (reseller.defaultEmail) {
          return reseller.defaultEmail
        }

        try {
          const resellerEmails =
            typeof reseller.emails === 'string' ? JSON.parse(reseller.emails) : reseller.emails
          if (Array.isArray(resellerEmails) && resellerEmails.length > 0) {
            return resellerEmails[0]
          }
        } catch (err) {
          // Error parsing reseller emails
        }
      }

      // Default logic for non-reseller customers
      if (!order?.customer?.emails) return undefined
      const emails =
        typeof order.customer.emails === 'string'
          ? JSON.parse(order.customer.emails)
          : order.customer.emails
      return Array.isArray(emails) && emails.length > 0 ? emails[0] : undefined
    } catch (error) {
      // Error parsing customer emails
      return undefined
    }
  }

  const { isInternalUser } = useInternalUserPermissions()

  return (
    <StyledContainer>
      {/* Customer Details Section */}
      <StyledCard>
        <StyledCardHeader title={<FormattedMessage id="customer-details" />} />
        <StyledCardContent>
          <StyledGridContainer spacing={3} container>
            {permission('customer') && order?.customer?.name && (
              <>
                <Grid item xs={12} md={4}>
                  <FieldLabel variant="subtitle2">Customer name:</FieldLabel>
                  <FieldValue variant="body1">
                    {order?.customer?.id ? (
                      <Link
                        component={RouterLink}
                        target="_blank"
                        to={`/customers/${order?.customer?.id}`}
                        rel="noreferrer"
                      >
                        {order?.customer?.name}
                      </Link>
                    ) : (
                      order?.customer?.name
                    )}
                    {!customerHasEmail && <NoProvidedCustomerEmail />}
                  </FieldValue>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FieldLabel variant="subtitle2">
                    <FormattedMessage id="customer-type" />:
                  </FieldLabel>
                  <FieldValue variant="body1">
                    {convertValuesToLabels(customerTypes, order?.customer?.type)}
                  </FieldValue>
                </Grid>
              </>
            )}
            {permission('assignee') && order.assignee?.firstName && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="assignee" />:
                </FieldLabel>
                <FieldValue variant="body1">
                  {order.assignee.firstName} {order.assignee.lastName}
                </FieldValue>
              </Grid>
            )}
          </StyledGridContainer>
        </StyledCardContent>
      </StyledCard>

      {/* Job Details Section */}
      <StyledCard>
        <StyledCardHeader
          title={<FormattedMessage id="job-details" />}
          action={
            <Box display="flex" alignItems="center" gap={1}>
              {order.freeFeed && (
                <Chip label="Free Feed" color="success" size="small" variant="outlined" />
              )}
              <ShareVacancy orderId={order.id} />
              {isInternalUser && <JobDetailsMenu order={order} />}
            </Box>
          }
        />
        <StyledCardContent>
          <StyledGridContainer container spacing={3}>
            {order.displayId && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="order-id" />:
                </FieldLabel>
                <FieldValue variant="body1">{order.displayId}</FieldValue>
              </Grid>
            )}

            {order.jobs && order.jobs.length > 0 && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">ATS Job:</FieldLabel>
                <FieldValue variant="body1">
                  <Link
                    component={RouterLink}
                    target="_blank"
                    to={`/jobs?jobId=${order.jobs[0].id}`}
                    rel="noreferrer"
                  >
                    {order.jobs[0].title}
                  </Link>
                </FieldValue>
              </Grid>
            )}

            {permission('jobName') && order.jobName && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="job-title" />:
                </FieldLabel>
                <FieldValue variant="body1">{order.jobName}</FieldValue>
              </Grid>
            )}

            {permission('status') && order.status && !isCustomer(userInfo?.role) && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="status" />:
                </FieldLabel>
                <FieldValue variant="body1">
                  {convertValuesToLabels(statuses, order.status)}
                </FieldValue>
              </Grid>
            )}

            {permission('jobType') && order.jobType && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="order-type" />:
                </FieldLabel>
                <FieldValue variant="body1">{order.jobType}</FieldValue>
              </Grid>
            )}

            {permission('place') && order.place && (
              <>
                <Grid item xs={12} md={4}>
                  <FieldLabel variant="subtitle2">
                    <FormattedMessage id="place" />:
                  </FieldLabel>
                  <FieldValue variant="body1">{order.place}</FieldValue>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FieldLabel variant="subtitle2">Portal:</FieldLabel>
                  <FieldValue variant="body1">{order.channel.join(', ')}</FieldValue>
                </Grid>
              </>
            )}

            {permission('jobAdLink') && order.jobAdLink && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="job-ad-link" />:
                </FieldLabel>
                <FieldValue variant="body1">
                  <StyledLink href={order.jobAdLink} target="_blank" rel="noreferrer">
                    {order.jobAdLink}
                  </StyledLink>
                </FieldValue>
              </Grid>
            )}

            {order.applyUrl && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="apply-link" />:
                </FieldLabel>
                <FieldValue variant="body1">
                  <StyledLink href={order.applyUrl} target="_blank" rel="noreferrer">
                    {order.applyUrl}
                  </StyledLink>
                </FieldValue>
              </Grid>
            )}

            {permission('externalImportId') && order.externalImportId && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="external-import-id" />:
                </FieldLabel>
                <FieldValue variant="body1">{order.externalImportId}</FieldValue>
              </Grid>
            )}

            {permission('keywords') && order.keywords && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="keywords" />:
                </FieldLabel>
                <FieldValue variant="body1">{order.keywords}</FieldValue>
              </Grid>
            )}

            {order.employmentType && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="employment-type" />:
                </FieldLabel>
                <FieldValue variant="body1">{order.employmentType}</FieldValue>
              </Grid>
            )}
          </StyledGridContainer>
        </StyledCardContent>
      </StyledCard>

      {/* Comments Section */}
      {permission('comments') && order.comments && (
        <StyledCard>
          <StyledCardHeader title={<FormattedMessage id="comments" />} />
          <StyledCardContent>
            <StyledGridContainer container spacing={2}>
              <Grid item xs={12}>
                <FieldValue variant="body1">{order.comments}</FieldValue>
              </Grid>
            </StyledGridContainer>
          </StyledCardContent>
        </StyledCard>
      )}

      {/* Budget Section */}
      <StyledCard>
        <StyledCardHeader title={<FormattedMessage id="budgets" />} />
        <StyledCardContent>
          <StyledGridContainer container spacing={3}>
            {permission('budget') && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="budget" />:
                </FieldLabel>
                <FieldValue variant="body1">{order.budget}</FieldValue>
              </Grid>
            )}

            {permission('price') && order.price && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="price" />:
                </FieldLabel>
                <FieldValue variant="body1">{order.price}</FieldValue>
              </Grid>
            )}

            {permission('runtime') && order.runtime && (
              <Grid item xs={12} md={4}>
                <FieldLabel variant="subtitle2">
                  <FormattedMessage id="runtime" />:
                </FieldLabel>
                <FieldValue variant="body1">{order.runtime}</FieldValue>
              </Grid>
            )}
          </StyledGridContainer>
        </StyledCardContent>
      </StyledCard>

      {/* Previews Section - simplified for this view */}
      {permission('previews') && previewFiles[0] && (
        <StyledCard>
          <StyledCardHeader
            title={<FormattedMessage id="preview" />}
            action={<SendPreviewLink orderId={order.id} customerEmail={getCustomerEmail()} />}
          />
          <StyledCardContent>
            <Box display="flex" alignItems="center" gap="4px" mb={2}>
              <Link component={RouterLink} target="_blank" to={`/order-previews?id=${order.id}`}>
                <FormattedMessage id="preview-all-link" />
              </Link>
              <CopyLinkBtn str={`/order-previews?id=${order.id}`} />
            </Box>
            <Box>
              {previewFiles.slice(0, 3).map((preview: TimageItem) => (
                <FileViewRow
                  key={preview?.image.url}
                  hasPreview
                  fileName={preview.name}
                  fileSource={preview?.image.url}
                />
              ))}
              {previewFiles.length > 3 && (
                <Typography variant="body2" color="primary">
                  +{previewFiles.length - 3} more files...
                </Typography>
              )}
            </Box>
          </StyledCardContent>
        </StyledCard>
      )}

      {/* Job File Section */}
      {order.jobFile && (
        <StyledCard>
          <StyledCardHeader title="Job File" />
          <StyledCardContent>
            <StyledGridContainer container spacing={2}>
              <Grid item xs={12}>
                <Box>
                  <FileViewRow
                    fileName={order.jobFile.name || 'Job File'}
                    fileSource={order.jobFile.file?.url}
                  />
                </Box>
              </Grid>
            </StyledGridContainer>
          </StyledCardContent>
        </StyledCard>
      )}

      {/* Video Assets Section */}
      {permission('videoAssets') && order.videoAssets[0] && (
        <StyledCard>
          <StyledCardHeader title={<FormattedMessage id="video-assets" />} />
          <StyledCardContent>
            <StyledGridContainer container spacing={2}>
              <Grid item xs={12}>
                <Box>
                  {order.videoAssets.map((video: TfileItem) => (
                    <FileViewRow
                      key={video.file.url}
                      fileName={video.name}
                      fileSource={video.file.url}
                    />
                  ))}
                </Box>
              </Grid>
            </StyledGridContainer>
          </StyledCardContent>
        </StyledCard>
      )}

      {/* Image Assets Section */}
      {permission('imageAssets') && order.imageAssets[0] && (
        <StyledCard>
          <StyledCardHeader title={<FormattedMessage id="image-assets" />} />
          <StyledCardContent>
            <StyledGridContainer container spacing={2}>
              <Grid item xs={12}>
                <Box>
                  {order.imageAssets.map((asset: any) => (
                    <Box key={asset.image.url} sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {asset.isHeader && (
                          <Chip
                            label="Header Image"
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      <FileViewRow fileName={asset.name} fileSource={asset.image.url} />
                    </Box>
                  ))}
                </Box>
              </Grid>
            </StyledGridContainer>
          </StyledCardContent>
        </StyledCard>
      )}

      {/* Ad Description Section - Moved to the end */}
      {order.adDescription && (
        <StyledCard>
          <StyledCardHeader title={<FormattedMessage id="ad-description" />} />
          <StyledCardContent>
            <Box>
              <div dangerouslySetInnerHTML={{ __html: order.adDescription }} />
            </Box>
          </StyledCardContent>
        </StyledCard>
      )}
    </StyledContainer>
  )
}

export default ReadOrderTree
